<template>
  <div class="tw-min-h-screen tw-py-16 tw-px-8 tw-flex tw-justify-center tw-bg-gray-100 tw-relative">
    <div
      v-if="isLoading"
      class="tw-fixed tw-top-0 tw-h-screen tw-w-full tw-bg-black tw-bg-opacity-40 tw-backdrop-blur-sm tw-flex tw-flex-col tw-justify-center tw-items-center tw-z-50"
    >
      <base-spinner :size="48" />
    </div>
    <div class="tw-max-w-xs tw-self-center tw-flex-col">
      <div class="tw-text-gray-800 tw-text-center">Enter your assigned PIN code.</div>
      <div class="tw-flex tw-justify-center tw-mt-6" @mousedown.stop.prevent="focusOnNextInputField">
        <div class="tw-flex">
          <input
            type="text"
            :class="[
              'tw-w-20 focus:tw-z-10 tw-border-gray-200 tw-border-t tw-border-b tw-border-r tw-rounded-none tw-text-center tw-text-3xl tw-py-3 tw-min-w-0',
              { 'tw-rounded-l-md': digit === 1 },
              { 'tw-border-l': digit === 1 },
              { 'tw-rounded-r-md': digit === numDigits },
            ]"
            v-for="digit in numDigits"
            :key="digit"
            :ref="`digit-input-${digit}`"
            :value="pin[digit - 1]"
            @keypress="isNumber"
            @input="addDigitToPin($event.data)"
            @keyup.backspace.prevent.stop="backspace"
          />
        </div>
      </div>
      <div class="tw-rounded-lg tw-mt-6 tw-bg-white tw-border tw-container">
        <div class="tw-grid tw-grid-cols-3">
          <div
            :class="[
              'tw-flex tw-justify-center tw-py-5 tw-border-b hover:tw-bg-gray-200 tw-text-2xl tw-cursor-pointer tw-select-none',
              { 'tw-border-r': digit % 3 !== 0 },
              { 'tw-rounded-tl-md': digit == 1 },
              { 'tw-rounded-tr-md': digit == 3 },
            ]"
            v-for="digit in 9"
            :key="digit"
            @click="addDigitToPin(digit)"
          >
            {{ digit }}
          </div>
          <div :class="['tw-flex tw-justify-center tw-py-5 tw-border-r hover:tw-bg-gray-200 tw-text-2xl tw-cursor-pointer tw-select-none tw-rounded-bl-md']">
            &nbsp;
          </div>
          <div @click="addDigitToPin(0)" :class="['tw-flex tw-justify-center tw-py-5 tw-border-r hover:tw-bg-gray-200 tw-text-2xl tw-cursor-pointer tw-select-none']">
            0
          </div>
          <div @click="backspace" :class="['tw-flex tw-justify-center tw-py-5 hover:tw-bg-gray-200 tw-text-2xl tw-cursor-pointer tw-select-none tw-rounded-br-md']">
            <backspace-icon class="tw-w-8 tw-h-8 tw-text-gray-800" />
          </div>
        </div>
      </div>
    </div>
    <global-error />
    <div class=" tw-absolute tw-bottom-4 tw-right-8">
      <base-button type="text" size="lg" color="primary" @click="$router.push('/signout')">
        <template v-slot:icon>
          <sign-out-icon />
        </template>
        Sign Out
      </base-button>
    </div>
  </div>
</template>

<script>
import GlobalError from "@/components/v2/GlobalError";
import { mapGetters, mapActions } from "vuex";
import { BackspaceIcon, SignOutIcon } from "@/assets/svgs";

export default {
  components: {
    GlobalError,
    BackspaceIcon,
    SignOutIcon,
  },
  data() {
    return {
      isLoading: false,
      numDigits: 4,
      pin: "",
    };
  },
  computed: {
    ...mapGetters("organization", ["organization", "isOrganizationBusiness", "isOrganizationUser"]),
  },
  methods: {
    ...mapActions("organization", { orgInit: "init" }),
    ...mapActions("role", { roleInit: "init" }),
    focusOnNextInputField() {
      if (this.pin.length < this.numDigits) {
        this.$refs[`digit-input-${this.pin.length + 1}`][0].focus();
      }
    },
    addDigitToPin(digit) {
      if (this.pin.length < this.numDigits && (digit || digit === 0)) {
        this.pin += digit;
        this.focusOnNextInputField();
      }
    },
    async loginWithPin() {
      try {
        this.isLoading = true;
        const response = await this.$axios.post(`/businesses/pin`, {
          pin: this.pin,
        });
        sessionStorage.removeItem("organization/id");
        sessionStorage.removeItem("hasStartedPinMode");
        sessionStorage.setItem(
          "pinMode",
          JSON.stringify({
            isAuthenticatedInPinMode: true,
            pinModeName: response.data.pinModeName,
          }),
        );
        this.$store.dispatch("cognito/initPinMode", JSON.parse(sessionStorage.pinMode));
        await Promise.all([this.orgInit(), this.roleInit()]);
        if (this.isOrganizationBusiness) {
          await this.$router.push(`/businesses/${this.organization.id}/dashboard`);
        } else if (this.isOrganizationUser) {
          await this.$router.push(`/users/${this.organization.id}/plans`);
        } else await this.$router.push("/");
      } catch (error) {
        if (error.response.status === 401) {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: "Invalid Pin",
            message: "The pin you submitted was invalid.",
            error: { message: "Invalid pin" },
          });
        } else {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: "Error submitting PIN",
            message: "There was an unexpected error.",
            error: { message: "unknown error" },
          });
        }
      } finally {
        this.pin = "";
        this.isLoading = false;
      }
    },
    handleKey(event) {
      if (event.keyCode >= 96 && event.keyCode <= 105) {
        this.addDigitToPin(event.keyCode);
        return true;
      } else {
        return false;
      }
    },
    isNumber(event) {
      let keyCode = event.keyCode ? event.keyCode : event.which;
      if (keyCode < 48 || keyCode > 57) {
        event.preventDefault();
      }
    },
    backspace() {
      this.pin = this.pin.slice(0, this.pin.length - 1);
      this.focusOnNextInputField();
    },
  },
  watch: {
    pin(newPin) {
      if (newPin.length === this.numDigits) {
        this.loginWithPin();
      }
    },
  },
};
</script>
